import { useEffect, useState } from 'react';
import { PARTS_FLOW_ENABLED } from '../../config/features';
import { getUserDetailsBaseUrl } from '../../utils/chatIdentity';
import { getSafeToDriveLevel, SAFE_TO_DRIVE_STYLES } from '../../utils/diagnosticReport';

const getDiagnosticReportTitle = (report = {}) => (
    report.issue || report.title || report.symptomSummary || report.symptom_summary || 'Vehicle diagnostic report'
);

const getDiagnosticReportVehicle = (report = {}) => (
    report.vehicle || report.vehicleLabel || report.vehicle_label || 'Selected vehicle'
);

const normalizeInventoryName = (value = '') => String(value || '').trim().toLowerCase();

const getDiagnosticReportStatus = (report = {}) => {
    const status = String(report.status || report.ticketStatus || '').toLowerCase();
    if (status.includes('closed')) return 'Resolved';
    if (status.includes('review') || status.includes('ready')) return 'Ready';
    return 'Ready';
};

const ReportIcon = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
        <path d="M14 2v5h5" />
        <path d="M8.5 13h7" />
        <path d="M8.5 17h5" />
    </svg>
);

const ArrowIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
    </svg>
);

const ReportChip = ({ value, label }) => (
    <span className="inline-flex min-h-[30px] items-center gap-1.5 rounded-full border border-[#e5e5e5] bg-white px-3 py-1 text-[11px] font-black text-[#414141]">
        <span className="tabular-nums">{value}</span>
        <span className="font-bold uppercase tracking-[0.08em] text-black/40">{label}</span>
    </span>
);

const DiagnosticReportShell = ({ eyebrow = 'Symptom Report', title, vehicle, status = 'Ready', children, actionLabel, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={`${actionLabel}: ${title}`}
        className="group mt-4 w-full overflow-hidden rounded-2xl border border-[#dbdbdb] bg-white text-left shadow-[0_12px_30px_rgba(48,48,48,0.10)] transition hover:-translate-y-0.5 hover:border-[#b2b2b2] hover:shadow-[0_18px_40px_rgba(48,48,48,0.16)] focus:outline-none focus:ring-2 focus:ring-[#D81F26]/50"
    >
        <div className="px-4 py-4">
            <div className="flex items-start justify-between gap-3">
                <div className="grid min-w-0 flex-1 grid-cols-[36px_minmax(0,1fr)] gap-x-3 gap-y-2">
                    <span className="row-span-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#f2f2f2] text-[#D81F26]">
                        <ReportIcon />
                    </span>
                    <div className="flex min-w-0 flex-wrap items-center gap-2 self-center">
                        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#757575]">{eyebrow}</span>
                        <span className="rounded-full border border-[#c8f0d6] bg-[#effbf3] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-[#15803d]">{status}</span>
                    </div>
                    <div className="col-start-2 min-w-0">
                        <h3 className="text-[16px] font-black leading-snug text-[#414141] sm:text-[18px]">{title}</h3>
                        <p className="mt-1 text-sm font-semibold text-black/50">{vehicle}</p>
                    </div>
                </div>
                <span className="mt-0.5 hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#181818] text-white transition group-hover:bg-black sm:flex">
                    <ArrowIcon />
                </span>
            </div>
            <div className="mt-3">
                {children}
                <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-[#f7f7f7] px-3 py-2.5">
                    <span className="text-[11px] font-black uppercase tracking-[0.13em] text-[#5a5a5a]">{actionLabel}</span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#181818] text-white transition group-hover:bg-black sm:hidden">
                        <ArrowIcon />
                    </span>
                </div>
            </div>
        </div>
    </button>
);

export const DiagnosticReportCard = ({ report, onOpen }) => (
    <DiagnosticReportShell
        title={getDiagnosticReportTitle(report)}
        vehicle={getDiagnosticReportVehicle(report)}
        status={getDiagnosticReportStatus(report)}
        actionLabel="Open full report"
        onClick={onOpen}
    >
        <div className="mt-3 flex flex-wrap items-center gap-2">
            <ReportChip value={Array.isArray(report?.likelyCauses) ? report.likelyCauses.length : 0} label="causes" />
            <ReportChip value={Array.isArray(report?.recommendedChecks) ? report.recommendedChecks.length : 0} label="checks" />
            <ReportChip value={report?.ticketNumber ? `#${report.ticketNumber}` : 'new'} label="ticket" />
        </div>
    </DiagnosticReportShell>
);

export const GenerateDiagnosticReportButton = ({ report, onClick }) => (
    <DiagnosticReportShell
        eyebrow="Symptom Report"
        title={getDiagnosticReportTitle(report)}
        vehicle={getDiagnosticReportVehicle(report)}
        status="Ready to build"
        actionLabel="Generate report"
        onClick={onClick}
    >
        <p className="mt-3 text-sm font-semibold leading-relaxed text-black/60">
            CarFido will organize this chat into symptoms, likely causes, checks, and next repair steps.
            This starts automatically in a moment, or tap below to generate it now.
        </p>
    </DiagnosticReportShell>
);

// ── Rich in-chat report card (shown after the diagnostic report is generated) ──
const getConfidenceBadge = (cause = {}) => {
    const raw = cause.confidence ?? cause.confidenceScore ?? cause.score;
    const num = typeof raw === 'number' ? raw : parseInt(String(raw || '').replace(/[^\d]/g, ''), 10);
    const difficulty = String(cause.difficulty || '').toLowerCase();
    let level = 'Medium';
    if (Number.isFinite(num)) level = num >= 70 ? 'High' : num >= 40 ? 'Medium' : 'Low';
    else if (difficulty.includes('easy')) level = 'High';
    else if (difficulty.includes('hard')) level = 'Low';
    const tone = level === 'High'
        ? 'bg-[#414141] text-white'
        : level === 'Low'
        ? 'bg-[#f4f4f4] text-[#414141]'
        : 'bg-[#fef3c7] text-[#b45309]';
    return { level, tone };
};

const getUrgency = (report = {}) => {
    const sev = `${report.severity || ''} ${report.readinessLabel || ''} ${report.safeToDrive || ''}`.toLowerCase();
    if (sev.includes('high') || String(report.safeToDrive || '').toLowerCase().includes('no')) {
        return { label: 'High Urgency', tone: 'bg-[#fee2e2] text-[#b91c1c]' };
    }
    if (sev.includes('low')) {
        return { label: 'Low Urgency', tone: 'bg-[#dcfce7] text-[#15803d]' };
    }
    return { label: 'Moderate Urgency', tone: 'bg-[#fef3c7] text-[#b45309]' };
};

const InlineSectionLabel = ({ children }) => (
    <p className="text-[10px] font-black uppercase tracking-[0.09em] text-[#414141]">{children}</p>
);

const InlinePillButton = ({ children, onClick, icon, primary = false, disabled = false }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={
            primary
                ? 'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#D81F26] px-4 py-3 text-[14px] font-black text-white shadow-sm transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-50'
                : 'inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-2 text-[13px] font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95'
        }
    >
        {icon}
        {children}
    </button>
);

const ToolChevron = ({ open }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={`text-[#414141] transition-transform ${open ? 'rotate-180' : ''}`}>
        <path d="m6 9 6 6 6-6" />
    </svg>
);

export const InlineDiagnosticReportCard = ({
    report,
    customerPhone,
    onViewReport,
    onBeginDiyCheck,
    onFindParts,
    onFindShop,
    findShopLabel = 'Find a Shop',
    referralShopName = '',
    referralSendStatus = '',
    referralSendError = '',
    onSendToReferralShop,
    onAskDash,
}) => {
    const [toolsOpen, setToolsOpen] = useState(false);
    const [confirmed, setConfirmed] = useState({});
    const [userOwnedTools, setUserOwnedTools] = useState([]);
    const [showForumModal, setShowForumModal] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const causes = Array.isArray(report?.likelyCauses) ? report.likelyCauses : [];
    const topCause = causes[0] || {};
    const otherCauses = causes.slice(1, 4);
    const vehicle = getDiagnosticReportVehicle(report);
    const symptom = report?.symptomSummary || report?.customerComplaint || report?.issue || getDiagnosticReportTitle(report);
    const urgency = getUrgency(report);
    const safeToDrive = report?.safeToDrive || '';
    const safetyMessage = report?.canDriveMessage || report?.safetyNote || '';
    const showSafety = Boolean(safeToDrive || safetyMessage);
    // Three-state color: No → red, Use caution → yellow, Yes → green (shared with
    // the full report page so both views match). Caution is not driven by urgency.
    const safeLevel = getSafeToDriveLevel(safeToDrive);
    const safeStyle = SAFE_TO_DRIVE_STYLES[safeLevel];
    // recommendedCheck may be a plain string OR a structured object
    // ({title, duration, toolsLabel, requiredTools, ...}) — handle both.
    const rc = topCause.recommendedCheck;
    const rcIsObj = rc && typeof rc === 'object';
    const recommendedCheck = rcIsObj
        ? (rc.title || rc.text || rc.description || '')
        : (rc || topCause.recommendedCheckTitle || '');
    const checkDuration = topCause.timeLabel
        || (rcIsObj ? rc.duration : '')
        || topCause.recommendedCheckDuration || topCause.checkDuration || '';

    const rawTools = Array.isArray(topCause.requiredTools)
        ? topCause.requiredTools
        : (rcIsObj && Array.isArray(rc.requiredTools) ? rc.requiredTools : []);
    const requiredTools = rawTools
        .map((tool) => {
            const obj = typeof tool === 'string' ? { name: tool } : { ...tool };
            let name = String(obj.name || '').trim();
            // A tool is optional if the backend flagged it, or the name ends with "(optional)".
            let optional = Boolean(obj.optional);
            if (/\(optional\)\s*$/i.test(name)) {
                optional = true;
                name = name.replace(/\s*\(optional\)\s*$/i, '').trim();
            }
            return { ...obj, name, optional };
        })
        .filter((tool) => tool.name);
    const essentialTools = requiredTools.filter((tool) => !tool.optional);
    const requiredToolNamesKey = JSON.stringify(requiredTools.map((tool) => tool.name));
    const effectivePhone = customerPhone || report?.customerPhone || report?.customer_phone || report?.phone_number || report?.phoneNumber || '';
    const toolSummary = topCause.toolSummary
        || (rcIsObj ? rc.toolsLabel : '')
        || requiredTools.map((tool) => tool.name).join(', ');

    useEffect(() => {
        if (!effectivePhone || requiredToolNamesKey === '[]') return;
        let cancelled = false;

        fetch(`${getUserDetailsBaseUrl()}/api/tools/${encodeURIComponent(effectivePhone)}`)
            .then((response) => response.json())
            .then((data) => {
                if (cancelled || !Array.isArray(data?.tools)) return;
                const tools = data.tools;
                setUserOwnedTools(tools);

                const ownedNames = new Set(tools.map((tool) => normalizeInventoryName(tool.name)));
                const requiredToolNames = JSON.parse(requiredToolNamesKey);
                setConfirmed((prev) => {
                    const next = { ...prev };
                    let changed = false;
                    requiredToolNames.forEach((toolName) => {
                        if (ownedNames.has(normalizeInventoryName(toolName)) && !next[toolName]) {
                            next[toolName] = true;
                            changed = true;
                        }
                    });
                    return changed ? next : prev;
                });
            })
            .catch(() => { /* checklist still works without inventory data */ });

        return () => { cancelled = true; };
    }, [effectivePhone, requiredToolNamesKey]);

    const toggleTool = (toolName) => {
        const wasChecked = Boolean(confirmed[toolName]);
        const nowChecked = !wasChecked;

        setConfirmed((prev) => ({
            ...prev,
            [toolName]: nowChecked,
        }));

        if (!effectivePhone) return;

        if (nowChecked) {
            fetch(`${getUserDetailsBaseUrl()}/api/tools`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phone_number: effectivePhone, name: toolName }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (!data?.tool) return;
                    setUserOwnedTools((prev) => {
                        const alreadyHasTool = prev.some(
                            (tool) => normalizeInventoryName(tool.name) === normalizeInventoryName(toolName)
                        );
                        return alreadyHasTool ? prev : [...prev, data.tool];
                    });
                })
                .catch(() => { /* inventory will refresh from the server later */ });
            return;
        }

        const ownedTool = userOwnedTools.find(
            (tool) => normalizeInventoryName(tool.name) === normalizeInventoryName(toolName)
        );
        if (!ownedTool?.tool_id) return;

        fetch(
            `${getUserDetailsBaseUrl()}/api/tools/${ownedTool.tool_id}?phone_number=${encodeURIComponent(effectivePhone)}`,
            { method: 'DELETE' }
        )
            .then((response) => response.json())
            .then((data) => {
                if (!data?.tool_id) return;
                setUserOwnedTools((prev) => prev.filter((tool) => tool.tool_id !== data.tool_id));
            })
            .catch(() => { /* silent */ });
    };


    const causeTitle = topCause.title || getDiagnosticReportTitle(report);

    // Top forum results shown in the "Forum Examples" popup (each opens its search).
    const _vehTokens = String(vehicle || '').trim().split(/\s+/).filter(Boolean);
    const _make = /^(19|20)\d{2}$/.test(_vehTokens[0] || '') ? (_vehTokens[1] || '') : (_vehTokens[0] || '');
    const _fq = (extra) => encodeURIComponent(`${vehicle} ${causeTitle} ${extra}`.trim());
    const forumResults = (Array.isArray(topCause.forumResults) && topCause.forumResults.length
        ? topCause.forumResults
        : [
            {
                title: `${vehicle}: ${causeTitle} discussion`,
                source: 'Reddit r/MechanicAdvice',
                description: 'Community diagnosis threads related to this cause.',
                url: `https://www.reddit.com/r/MechanicAdvice/search/?q=${_fq('')}&sort=relevance`,
            },
            {
                title: `${vehicle} ${causeTitle}: owner threads`,
                source: 'Reddit r/AskMechanics',
                description: 'Real owners describing the same symptom and fixes.',
                url: `https://www.reddit.com/r/AskMechanics/search/?q=${_fq('')}&sort=relevance`,
            },
            {
                title: `${vehicle} ${causeTitle} forum`,
                source: 'Google Forums Search',
                description: 'Vehicle-specific forum threads on this diagnosis.',
                url: `https://www.google.com/search?q=${_fq('forum')}`,
            },
            {
                title: `${_make || 'Owner'} owners forum: ${causeTitle}`,
                source: 'Owner Community',
                description: 'Model-specific owner community discussions.',
                url: `https://www.google.com/search?q=${_fq('owners forum')}`,
            },
            {
                title: `${vehicle} ${causeTitle}: reported problems`,
                source: 'Complaints & Q&A',
                description: 'Documented complaints and mechanic answers.',
                url: `https://www.google.com/search?q=${_fq('problem OR complaint OR fix')}`,
            },
        ]
    ).slice(0, 5);

    // Top video tutorials shown in the "YouTube Tutorial" popup (each opens a YouTube search).
    const _yt = (extra) => `https://www.youtube.com/results?search_query=${encodeURIComponent(`${vehicle} ${causeTitle} ${extra}`.trim())}`;
    const videoResults = (Array.isArray(topCause.videoResults) && topCause.videoResults.length
        ? topCause.videoResults
        : [
            { title: `${vehicle} ${causeTitle}: diagnosis`, source: 'YouTube', description: 'Step-by-step diagnosis walkthroughs.', url: _yt('diagnosis') },
            { title: `${vehicle} ${causeTitle}: how to fix`, source: 'YouTube', description: 'Repair and replacement tutorials.', url: _yt('how to fix replace') },
            { title: `${vehicle} ${causeTitle}: symptoms`, source: 'YouTube', description: 'What the problem looks and sounds like.', url: _yt('symptoms sound') },
            { title: `${recommendedCheck || causeTitle}: how to test`, source: 'YouTube', description: 'How to perform the recommended check.', url: `https://www.youtube.com/results?search_query=${encodeURIComponent(`${vehicle} ${recommendedCheck || causeTitle} test how to`.trim())}` },
            { title: `${_make || 'Owner'} ${causeTitle}: repair guide`, source: 'YouTube', description: 'Model-specific repair guides.', url: _yt('repair guide') },
        ]
    ).slice(0, 5);

    return (
        <div className="not-prose mt-4 w-full max-w-[420px] overflow-hidden rounded-2xl border border-[#dbdbdb] bg-white font-nunito text-[#414141] shadow-[0_10px_26px_rgba(48,48,48,0.10)]">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 border-b border-[#f2f2f2] px-4 py-2.5">
                <div className="min-w-0">
                    <p className="text-[10.5px] font-black uppercase tracking-[0.12em] text-[#D81F26]">Symptom Report Ready</p>
                    <p className="mt-0.5 truncate text-[13px] font-bold text-[#414141]">{vehicle}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9.5px] font-black uppercase tracking-[0.05em] ${urgency.tone}`}>
                    {urgency.label}
                </span>
            </div>

            <div className="space-y-2.5 px-4 py-3">
                {/* Symptom */}
                {symptom && (
                    <div>
                        <InlineSectionLabel>Symptom</InlineSectionLabel>
                        <p className="mt-1 text-[13px] font-semibold leading-snug text-[#414141]">{symptom}</p>
                    </div>
                )}

                {/* Most likely cause */}
                {topCause.title && (
                    <div>
                        <InlineSectionLabel>Most Likely Cause</InlineSectionLabel>
                        <div className="mt-1 flex items-center gap-2.5">
                            <span className={`inline-flex w-[52px] shrink-0 items-center justify-center rounded-full py-0.5 text-[10px] font-black ${getConfidenceBadge(topCause).tone}`}>
                                {getConfidenceBadge(topCause).level}
                            </span>
                            <p className="text-[13.5px] font-black leading-snug text-[#414141]">{topCause.title}</p>
                        </div>
                        {topCause.why && (
                            <p className="mt-1 text-[12px] font-medium leading-relaxed text-[#606060]">{topCause.why}</p>
                        )}
                    </div>
                )}

                {/* Also possible */}
                {otherCauses.length > 0 && (
                    <div>
                        <InlineSectionLabel>Also Possible</InlineSectionLabel>
                        <div className="mt-1 space-y-1">
                            {otherCauses.map((cause, index) => {
                                const badge = getConfidenceBadge(cause);
                                return (
                                    <div key={index} className="flex items-center gap-2.5">
                                        <span className={`inline-flex w-[52px] shrink-0 items-center justify-center rounded-full py-0.5 text-[10px] font-black ${badge.tone}`}>
                                            {badge.level}
                                        </span>
                                        <p className="text-[12px] font-semibold leading-snug text-[#494949]">{cause.title}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Is it safe to drive */}
                {showSafety && (
                    <div>
                        <InlineSectionLabel>Is It Safe To Drive?</InlineSectionLabel>
                        <div className="mt-1.5 flex gap-2 rounded-xl border px-3 py-2.5" style={{ borderColor: safeStyle.border, backgroundColor: safeStyle.bg }}>
                            <svg className="mt-0.5 h-4 w-4 shrink-0" style={{ color: safeStyle.icon }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                            <div>
                                {safeToDrive && (
                                    <p className="text-[12px] font-black" style={{ color: safeStyle.heading }}>{safeToDrive}</p>
                                )}
                                {safetyMessage && (
                                    <p className="mt-0.5 text-[12px] font-medium leading-relaxed text-[#414141]">{safetyMessage}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Recommended next step */}
                {recommendedCheck && (
                    <div>
                        <InlineSectionLabel>Recommended Next Step</InlineSectionLabel>
                        <div className="mt-1.5 rounded-xl border border-[#e6e6e6] bg-[#f9f9f9] px-3 py-2.5">
                            <p className="text-[13.5px] font-black text-[#414141]">{recommendedCheck}</p>
                            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] font-bold text-[#414141]">
                                {checkDuration && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
                                        {checkDuration}
                                    </span>
                                )}
                                {toolSummary && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.1-3.1a6 6 0 0 1-7.9 7.9l-6 6a2 2 0 0 1-2.8-2.8l6-6a6 6 0 0 1 7.9-7.9l-3.1 3.1z" /></svg>
                                        {toolSummary}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Required tools & parts (accordion) */}
                {requiredTools.length > 0 && (
                    <div className="overflow-hidden rounded-xl border border-[#e6e6e6]">
                        <button
                            type="button"
                            onClick={() => setToolsOpen((prev) => !prev)}
                            className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left"
                        >
                            <span className="flex items-center gap-2 text-[14px] font-black text-[#414141]">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15" /><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>
                                Tools &amp; parts
                            </span>
                            <span className="flex items-center gap-2 text-[12px] font-bold text-[#414141]">
                                {essentialTools.length} needed{requiredTools.length > essentialTools.length ? `, ${requiredTools.length - essentialTools.length} optional` : ''}
                                <ToolChevron open={toolsOpen} />
                            </span>
                        </button>
                        {toolsOpen && (
                            <div className="border-t border-[#e6e6e6]">
                                {requiredTools.map((tool, index) => {
                                    const isOwned = userOwnedTools.length > 0
                                        ? userOwnedTools.some((ownedTool) => normalizeInventoryName(ownedTool.name) === normalizeInventoryName(tool.name))
                                        : undefined;
                                    return (
                                        <label
                                            key={tool.name}
                                            className={`flex cursor-pointer items-center gap-3 px-3.5 py-2.5 ${index === 0 ? '' : 'border-t border-[#f2f2f2]'}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={Boolean(confirmed[tool.name])}
                                                onChange={() => toggleTool(tool.name)}
                                                className="h-4 w-4 rounded border-[#c8c8c8] text-[#D81F26] focus:ring-[#D81F26]"
                                            />
                                            <span className="min-w-0 flex-1 text-[14px] font-semibold text-[#414141]">
                                                {tool.name}
                                                {tool.optional && (
                                                    <span className="ml-2 rounded-full bg-[#f2f2f2] px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.04em] text-[#414141]">Optional</span>
                                                )}
                                            </span>
                                            {isOwned && (
                                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#dff7e7] px-2 py-0.5 text-[10px] font-black text-[#0a8f53]">
                                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M20 6 9 17l-5-5" />
                                                    </svg>
                                                    You own this
                                                </span>
                                            )}
                                            {isOwned === false && (
                                                <span className="inline-flex shrink-0 rounded-full bg-[#fff5e8] px-2 py-0.5 text-[10px] font-black text-[#b7791f]">
                                                    Not in inventory
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Before you start */}
                <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Before You Start</p>
                    <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-[#414141]">
                        {report?.diagnosisStage === 'Maintenance'
                            ? "Grab what you have and begin. I'll walk you through the service one step at a time."
                            : essentialTools.length > 0
                            ? "Grab what you have and begin. I'll tell you exactly what each step needs. Tick tools you own to save them to your inventory (optional)."
                            : 'Start the guided check to walk through the inspection step by step.'}
                    </p>
                    {onBeginDiyCheck && (
                        <button
                            type="button"
                            onClick={() => onBeginDiyCheck(report)}
                            className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#D81F26] px-5 py-3 text-[14px] font-black text-white shadow-sm transition hover:bg-[#B8181E] active:scale-[0.99]"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                            {report?.diagnosisStage === 'Maintenance' ? 'Start DIY Steps' : 'Begin DIY Check'}
                        </button>
                    )}
                </div>

                {referralShopName && onSendToReferralShop && (
                    <div className="rounded-2xl border border-[#f2c7c9] bg-[#fff7f7] p-4" data-testid="referral-report-prompt">
                        {referralSendStatus === 'sent' ? (
                            <div className="flex items-start gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e8fbef] text-[#087a43]" aria-hidden="true">✓</span>
                                <div>
                                    <p className="text-[14px] font-black text-[#414141]">Report sent to {referralShopName}</p>
                                    <p className="mt-1 text-[12px] font-semibold leading-relaxed text-[#606060]">They can review it and reply through your shop conversation.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-[14px] font-black text-[#414141]">Send this report back to {referralShopName}?</p>
                                <p className="mt-1 text-[12px] font-semibold leading-relaxed text-[#606060]">This shop referred you. Send the completed report directly to them so they can review it and follow up.</p>
                                <button
                                    type="button"
                                    onClick={() => onSendToReferralShop(report)}
                                    disabled={referralSendStatus === 'sending'}
                                    className="mt-3 flex w-full items-center justify-center rounded-xl bg-[#D81F26] px-4 py-3 text-[14px] font-black text-white transition hover:bg-[#B8181E] disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {referralSendStatus === 'sending' ? 'Sending report…' : `Send report to ${referralShopName}`}
                                </button>
                                {referralSendError ? <p role="alert" className="mt-2 text-[12px] font-bold text-[#b42318]">{referralSendError}</p> : null}
                            </>
                        )}
                    </div>
                )}

                {/* Secondary actions */}
                <div className="flex flex-wrap gap-2">
                    {onViewReport && (
                        <InlinePillButton
                            onClick={(event) => onViewReport(report, event)}
                            icon={(<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" /><path d="M14 2v5h5" /></svg>)}
                        >
                            View Report
                        </InlinePillButton>
                    )}
                    {PARTS_FLOW_ENABLED && onFindParts && (
                        <InlinePillButton
                            onClick={() => onFindParts(report)}
                            icon={(<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></svg>)}
                        >
                            Find Parts
                        </InlinePillButton>
                    )}
                    {onFindShop && !referralShopName && (
                        <InlinePillButton
                            onClick={() => onFindShop(report)}
                            icon={(<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" /></svg>)}
                        >
                            {findShopLabel}
                        </InlinePillButton>
                    )}
                </div>

                {/* Need help first */}
                <div className="border-t border-[#f2f2f2] pt-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#414141]">Need Help First?</p>
                    <p className="mt-1.5 text-[13px] font-semibold text-[#414141]">
                        Review real-world examples or watch a tutorial before starting.
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <button
                            type="button"
                            onClick={() => setShowForumModal(true)}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-2.5 text-[13px] font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                            Forum Examples
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowVideoModal(true)}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-3.5 py-2.5 text-[13px] font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#ef4444" stroke="none"><path d="M23 12s0-3.5-.45-5.18a2.6 2.6 0 0 0-1.83-1.84C19.05 4.5 12 4.5 12 4.5s-7.05 0-8.72.48A2.6 2.6 0 0 0 1.45 6.82C1 8.5 1 12 1 12s0 3.5.45 5.18a2.6 2.6 0 0 0 1.83 1.84C4.95 19.5 12 19.5 12 19.5s7.05 0 8.72-.48a2.6 2.6 0 0 0 1.83-1.84C23 15.5 23 12 23 12z" /><path d="M9.75 15.5v-7l6 3.5z" fill="#fff" /></svg>
                            YouTube Tutorial
                        </button>
                    </div>
                    {onAskDash && (
                        <button
                            type="button"
                            onClick={() => onAskDash(report)}
                            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-[#d9d9d9] bg-white px-4 py-2.5 text-[13px] font-black text-[#414141] transition hover:bg-[#F9F9F9] active:scale-95"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                            Ask CarFido
                        </button>
                    )}
                </div>
            </div>

            {/* Forum Examples popup — top 5 forum results (each opens its search) */}
            {showForumModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 font-nunito"
                    onClick={() => setShowForumModal(false)}
                >
                    <div
                        className="w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(48,48,48,0.28)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between gap-3 border-b border-[#f2f2f2] px-4 py-3.5">
                            <div className="flex items-center gap-2">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#414141" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                                <span className="text-[16px] font-black text-[#414141]">Real-world Examples</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowForumModal(false)}
                                aria-label="Close"
                                className="flex h-7 w-7 items-center justify-center rounded-full text-[#414141] transition hover:bg-[#f4f4f4]"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                            </button>
                        </div>

                        {/* Cause subtitle */}
                        <p className="px-4 pt-3 text-[13px] font-semibold text-[#414141]">{causeTitle}</p>

                        {/* Results */}
                        <div className="max-h-[60vh] overflow-y-auto px-4 pb-1">
                            {forumResults.map((result, index) => (
                                <a
                                    key={`${result.title}-${index}`}
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`group flex items-start justify-between gap-3 py-3 ${index === forumResults.length - 1 ? '' : 'border-b border-[#f4f4f4]'}`}
                                >
                                    <div className="min-w-0">
                                        <p className="text-[15px] font-black leading-snug text-[#414141] group-hover:text-[#D81F26]">{result.title}</p>
                                        <p className="mt-0.5 text-[11px] font-black uppercase tracking-[0.06em] text-[#6B6B6B]">{result.source}</p>
                                        <p className="mt-0.5 text-[13px] font-medium text-[#414141]">{result.description}</p>
                                    </div>
                                    <svg className="mt-0.5 shrink-0 text-[#414141] group-hover:text-[#D81F26]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
                                </a>
                            ))}
                        </div>

                        {/* Footer disclaimer */}
                        <div className="border-t border-[#f2f2f2] px-4 py-3">
                            <p className="text-[12px] font-medium leading-relaxed text-[#939393]">
                                Forum posts are community examples, not verified repair instructions. Use CarFido's guided checks to confirm your vehicle's issue.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* YouTube Tutorial popup — top 5 video tutorials (each opens a YouTube search) */}
            {showVideoModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 font-nunito"
                    onClick={() => setShowVideoModal(false)}
                >
                    <div
                        className="w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_rgba(48,48,48,0.28)]"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3 border-b border-[#f2f2f2] px-4 py-3.5">
                            <div className="flex items-center gap-2">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="#ef4444" stroke="none"><path d="M23 12s0-3.5-.45-5.18a2.6 2.6 0 0 0-1.83-1.84C19.05 4.5 12 4.5 12 4.5s-7.05 0-8.72.48A2.6 2.6 0 0 0 1.45 6.82C1 8.5 1 12 1 12s0 3.5.45 5.18a2.6 2.6 0 0 0 1.83 1.84C4.95 19.5 12 19.5 12 19.5s7.05 0 8.72-.48a2.6 2.6 0 0 0 1.83-1.84C23 15.5 23 12 23 12z" /><path d="M9.75 15.5v-7l6 3.5z" fill="#fff" /></svg>
                                <span className="text-[16px] font-black text-[#414141]">Video Tutorials</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowVideoModal(false)}
                                aria-label="Close"
                                className="flex h-7 w-7 items-center justify-center rounded-full text-[#414141] transition hover:bg-[#f4f4f4]"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                            </button>
                        </div>
                        <p className="px-4 pt-3 text-[13px] font-semibold text-[#414141]">{causeTitle}</p>
                        <div className="max-h-[60vh] overflow-y-auto px-4 pb-1">
                            {videoResults.map((result, index) => (
                                <a
                                    key={`${result.title}-${index}`}
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`group flex items-start justify-between gap-3 py-3 ${index === videoResults.length - 1 ? '' : 'border-b border-[#f4f4f4]'}`}
                                >
                                    <div className="flex min-w-0 items-start gap-2.5">
                                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#fee2e2]">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444" stroke="none"><path d="M23 12s0-3.5-.45-5.18a2.6 2.6 0 0 0-1.83-1.84C19.05 4.5 12 4.5 12 4.5s-7.05 0-8.72.48A2.6 2.6 0 0 0 1.45 6.82C1 8.5 1 12 1 12s0 3.5.45 5.18a2.6 2.6 0 0 0 1.83 1.84C4.95 19.5 12 19.5 12 19.5s7.05 0 8.72-.48a2.6 2.6 0 0 0 1.83-1.84C23 15.5 23 12 23 12z" /><path d="M9.75 15.5v-7l6 3.5z" fill="#fff" /></svg>
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-[15px] font-black leading-snug text-[#414141] group-hover:text-[#D81F26]">{result.title}</p>
                                            <p className="mt-0.5 text-[11px] font-black uppercase tracking-[0.06em] text-[#c0392b]">{result.source}</p>
                                            <p className="mt-0.5 text-[13px] font-medium text-[#414141]">{result.description}</p>
                                        </div>
                                    </div>
                                    <svg className="mt-0.5 shrink-0 text-[#414141] group-hover:text-[#D81F26]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
                                </a>
                            ))}
                        </div>
                        <div className="border-t border-[#f2f2f2] px-4 py-3">
                            <p className="text-[12px] font-medium leading-relaxed text-[#939393]">
                                Videos are community tutorials, not verified repair instructions. Use CarFido's guided checks to confirm your vehicle's issue.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
